"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Sidebar from "../SidebarInAdminPanel/Sidebar"
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaIdCard,
  FaCalendarAlt,
  FaCheck,
  FaTimes,
  FaArrowLeft,
  FaUserTag,
  FaSearch,
  FaDownload,
  FaClipboardList,
  FaSpinner,
  FaArrowUp,
  FaArrowDown,
  FaSync,
} from "react-icons/fa"

import { doc, getDoc, updateDoc, deleteDoc, collection, query, orderBy, getDocs } from "firebase/firestore"
import { firestore } from "../../backend/firebaseConfig"
import { deleteAdminByEmail } from "../../Controllers/adminController"
import LoadingPage from "../Loading/Loading"

// Mock data for user requests
const MOCK_REQUESTS = [
  {
    id: "REQ-002",
    type: "Ride Request",
    status: "pending",
    priority: "medium",
    category: "Transportation",
    createdAt: new Date(2023, 5, 20).toISOString(),
    description: "Need a ride from University to Shopping Mall",
    from: "National University",
    to: "Centaurus Mall",
    date: "2023-06-20",
    time: "16:30",
    fare: "800",
    vehicleType: "Hatchback",
  },
  {
    id: "REQ-004",
    type: "Ride Request",
    status: "cancelled",
    priority: "high",
    category: "Transportation",
    createdAt: new Date(2023, 5, 10).toISOString(),
    description: "Emergency ride to hospital",
    from: "F-10 Markaz",
    to: "PIMS Hospital",
    date: "2023-06-10",
    time: "08:15",
    fare: "950",
    vehicleType: "SUV",
  },
]

const UserDetailsPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [expandedImage, setExpandedImage] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [transactionsLoading, setTransactionsLoading] = useState(false)
  const [userBalance, setUserBalance] = useState(0)

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const docRef = doc(firestore, "users", id)
        const docSnap = await getDoc(docRef)
        if (docSnap.exists()) {
          // Add mock requests to the user data
          setUser({
            id: docSnap.id,
            ...docSnap.data(),
            requests: MOCK_REQUESTS, // Add mock requests
          })
          // Fetch transactions after user data is loaded
          await fetchUserTransactions(id)
        } else {
          setError("User not found.")
        }
      } catch (error) {
        console.error("Error fetching user:", error)
        setError("Failed to fetch user data.")
      }
      setLoading(false)
    }

    fetchUser()
  }, [id])

  const fetchUserTransactions = async (userId) => {
    try {
      setTransactionsLoading(true)
      
      // Fetch user's current balance
      const userPaymentDocRef = doc(firestore, "Payments", userId)
      const userPaymentDoc = await getDoc(userPaymentDocRef)
      
      if (userPaymentDoc.exists()) {
        setUserBalance(userPaymentDoc.data().currentBalance || 0)
      }

      // Fetch user's transactions
      const transactionsRef = collection(userPaymentDocRef, "transactions")
      const q = query(transactionsRef, orderBy("createdAt", "desc"))
      const transactionsSnapshot = await getDocs(q)

      const userTransactions = transactionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))

      setTransactions(userTransactions)
    } catch (error) {
      console.error("Error fetching user transactions:", error)
      setTransactions([]) // Set empty array on error
    } finally {
      setTransactionsLoading(false)
    }
  }

  const formatTransactionDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const getTransactionIcon = (type) => {
    return type === 'credit' ? <FaArrowUp className="text-green-500" /> : <FaArrowDown className="text-red-500" />
  }

  const getTransactionColor = (type) => {
    return type === 'credit' ? 'text-green-600' : 'text-red-600'
  }

  const handleVerifyUser = async () => {
    if (!user) return
    try {
      const docRef = doc(firestore, "users", user.id)
      await updateDoc(docRef, { isverified: true })
      setUser((prev) => ({ ...prev, isverified: true }))
      alert("User verified successfully!")
    } catch (error) {
      console.error("Error updating user:", error)
      alert("Failed to verify user. Please try again.")
    }
  }

  const handleDeleteUser = async () => {
    if (!user) return
    try {
      const docRef = doc(firestore, "users", user.id)
      console.log("Deleting user:", user.email)
      await deleteAdminByEmail(user.email)
      await deleteDoc(docRef)
      alert("User deleted successfully!")
      navigate("/user-management")
    } catch (error) {
      console.error("Error deleting user:", error)
      alert("Failed to delete user. Please try again.")
    }
  }

  const handleImageClick = (imageUrl) => {
    setExpandedImage(imageUrl)
  }

  const handleCloseExpandedImage = () => {
    setExpandedImage(null)
  }

  const handleDownloadImage = (imageUrl, imageName) => {
    const link = document.createElement("a")
    link.href = imageUrl
    link.download = `${user.fullName}-${imageName}.jpg`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleRefreshTransactions = () => {
    fetchUserTransactions(id)
  }

  if (loading) {
    return <LoadingPage />
  }

  if (error || !user) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-xl text-red-500 mb-4">{error || "User not found"}</p>
        <button
          onClick={() => navigate("/users")}
          className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        >
          Back to User List
        </button>
      </div>
    )
  }

  // const requestCounts = {
  //   all: user.requests?.length || 0,
  //   pending: user.requests?.filter((r) => r.status === "pending").length || 0,
  //   completed: user.requests?.filter((r) => r.status === "completed").length || 0,
  //   cancelled: user.requests?.filter((r) => r.status === "cancelled").length || 0,
  //   "in-progress": user.requests?.filter((r) => r.status === "in-progress").length || 0,
  // }

  return (
    <div className="flex bg-gradient-to-r from-[#0AD1C8] to-[#073235] min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8">
        {/* Top Bar */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between lg:justify-start gap-4 lg:gap-20">
  <button
    onClick={() => navigate("/user-management")}
    className="flex items-center text-white text-lg font-semibold transform transition-transform duration-300 hover:scale-105 hover:text-yellow-300"
  >
    <FaArrowLeft className="mr-2" /> Back to Users
  </button>

  {/* Navigation Buttons - Hidden on small screens, shown on large screens */}
  <div className="hidden lg:flex lg:ml-25 items-center bg-white rounded-full px-4 py-2 shadow-md gap-3 ml-16">
    <button
      onClick={() => document.getElementById("documents-section").scrollIntoView({ behavior: "smooth" })}
      className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-full shadow-md transition-all font-semibold text-sm"
    >
      Document Verification
    </button>
    <button
      onClick={() => document.getElementById("requests-section").scrollIntoView({ behavior: "smooth" })}
      className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-full shadow-md transition-all font-semibold text-sm"
    >
      View Requests
    </button>
    <button
      onClick={() => document.getElementById("transactions-section").scrollIntoView({ behavior: "smooth" })}
      className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-full shadow-md transition-all font-semibold text-sm"
    >
      Balance & Transactions
    </button>
  </div>
</div>

          <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-md self-start sm:self-auto">
            {user.isverified ? <FaCheck className="text-green-500 mr-2" /> : <FaTimes className="text-red-500 mr-2" />}
            <span className={`text-sm font-semibold ${user.isverified ? "text-green-600" : "text-red-600"}`}>
              {user.isverified ? "Verified" : "Not Verified"}
            </span>
          </div>
        </div>

        {/* Navigation Buttons - Shown on small screens, hidden on large screens */}
        <div className="flex justify-center mb-8 lg:hidden">
          <div className="bg-gradient-to-r from-cyan-100 to-blue-100 p-4 rounded-2xl shadow-lg flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => document.getElementById("documents-section").scrollIntoView({ behavior: "smooth" })}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-5 py-2 rounded-full shadow-md transition-all font-semibold"
            >
              Document Verification
            </button>
            <button
              onClick={() => document.getElementById("requests-section").scrollIntoView({ behavior: "smooth" })}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-5 py-2 rounded-full shadow-md transition-all font-semibold"
            >
              View Requests
            </button>
            <button
              onClick={() => document.getElementById("transactions-section").scrollIntoView({ behavior: "smooth" })}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-5 py-2 rounded-full shadow-md transition-all font-semibold"
            >
              Balance & Transactions
            </button>
          </div>
        </div>

        {/* Main Content Box */}
        <div className="bg-white p-8 rounded-xl shadow-lg">
          {/* User Info & Ratings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10">
            <div className="bg-green-200 border-blue-200 shadow-md rounded-2xl p-6 space-y-3">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">User Details</h3>

              <p className="text-gray-700">
                <FaUser className="inline mr-2 text-teal-500" />
                <strong>Full Name:</strong> {user.fullName}
              </p>
              <p className="text-gray-700">
                <FaPhone className="inline mr-2 text-teal-500" />
                <strong>Phone:</strong> {user.phone}
              </p>
              <p className="text-gray-700">
                <FaEnvelope className="inline mr-2 text-teal-500" />
                <strong>Email:</strong> {user.email}
              </p>
              <p className="text-gray-700">
                <FaUserTag className="inline mr-2 text-teal-500" />
                <strong>User Type:</strong> {user.userType}
              </p>
              <p className="text-gray-700">
                <FaIdCard className="inline mr-2 text-teal-500" />
                <strong>CNIC:</strong> {user.cnic}
              </p>
              <p className="text-gray-700">
                <FaCalendarAlt className="inline mr-2 text-teal-500" />
                <strong>Created At:</strong> {new Date(user.createdAt).toLocaleString()}
              </p>
            </div>

            <div className="bg-green-200 border-blue-200 shadow-md rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-4">Ratings & Reviews (273)</h3>
              <div className="space-y-1">
                {[5, 4, 3, 2, 1].map((star, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-20 text-sm">{star} Star</div>
                    <div className="flex-1 bg-white rounded h-2 mx-2 overflow-hidden">
                      <div className={`bg-yellow-400 h-2`} style={{ width: `${(5 - index) * 20}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4 text-lg font-semibold">
                <div>
                  <span className="text-2xl">4.5</span>
                  <span className="text-2xl ml-1 text-yellow-400">★</span>
                </div>
                <div>88% Recommended</div>
              </div>
              <div className="mt-6 text-left">
                <button
                  onClick={() => navigate(`/user/${id}/reviews`)}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-all"
                >
                  View All Reviews
                </button>
              </div>
            </div>
          </div>

          {/* Verification Documents Section */}
          <div id="documents-section" className="mb-10 scroll-mt-24">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Verification Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {user.frontNIDImage && (
                <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-xl overflow-hidden shadow-lg border-2 border-teal-300 transition-all hover:shadow-xl">
                  <div className="bg-gradient-to-r from-teal-500 to-green-500 text-white py-3 px-4">
                    <h3 className="text-center font-semibold">Front NID Image</h3>
                  </div>
                  <div className="p-4">
                    <div className="relative overflow-hidden rounded-lg group">
                      <img
                        src={user.frontNIDImage || "/placeholder.svg"}
                        alt="Front NID"
                        className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                        <div className="flex space-x-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <button
                            onClick={() => handleImageClick(user.frontNIDImage)}
                            className="bg-white p-2 rounded-full hover:bg-teal-100 transition-colors"
                            aria-label="View image"
                          >
                            <FaSearch className="text-teal-600" />
                          </button>
                          <button
                            onClick={() => handleDownloadImage(user.frontNIDImage, "front-nid")}
                            className="bg-white p-2 rounded-full hover:bg-teal-100 transition-colors"
                            aria-label="Download image"
                          >
                            <FaDownload className="text-teal-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-center text-sm text-gray-600">Click to view or download</div>
                  </div>
                </div>
              )}

              {user.backNIDImage && (
                <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-xl overflow-hidden shadow-lg border-2 border-teal-300 transition-all hover:shadow-xl">
                  <div className="bg-gradient-to-r from-teal-500 to-green-500 text-white py-3 px-4">
                    <h3 className="text-center font-semibold">Back NID Image</h3>
                  </div>
                  <div className="p-4">
                    <div className="relative overflow-hidden rounded-lg group">
                      <img
                        src={user.backNIDImage || "/placeholder.svg"}
                        alt="Back NID"
                        className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                        <div className="flex space-x-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <button
                            onClick={() => handleImageClick(user.backNIDImage)}
                            className="bg-white p-2 rounded-full hover:bg-teal-100 transition-colors"
                            aria-label="View image"
                          >
                            <FaSearch className="text-teal-600" />
                          </button>
                          <button
                            onClick={() => handleDownloadImage(user.backNIDImage, "back-nid")}
                            className="bg-white p-2 rounded-full hover:bg-teal-100 transition-colors"
                            aria-label="Download image"
                          >
                            <FaDownload className="text-teal-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-center text-sm text-gray-600">Click to view or download</div>
                  </div>
                </div>
              )}

              {user.faceVerificationImage && (
                <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-xl overflow-hidden shadow-lg border-2 border-teal-300 transition-all hover:shadow-xl">
                  <div className="bg-gradient-to-r from-teal-500 to-green-500 text-white py-3 px-4">
                    <h3 className="text-center font-semibold">Face Verification Image</h3>
                  </div>
                  <div className="p-4">
                    <div className="relative overflow-hidden rounded-lg group">
                      <img
                        src={user.faceVerificationImage || "/placeholder.svg"}
                        alt="Face Verification"
                        className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                        <div className="flex space-x-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <button
                            onClick={() => handleImageClick(user.faceVerificationImage)}
                            className="bg-white p-2 rounded-full hover:bg-teal-100 transition-colors"
                            aria-label="View image"
                          >
                            <FaSearch className="text-teal-600" />
                          </button>
                          <button
                            onClick={() => handleDownloadImage(user.faceVerificationImage, "face-verification")}
                            className="bg-white p-2 rounded-full hover:bg-teal-100 transition-colors"
                            aria-label="Download image"
                          >
                            <FaDownload className="text-teal-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-center text-sm text-gray-600">Click to view or download</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div id="requests-section" className="mb-10 scroll-mt-24">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-md">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">User Requests</h3>
                  <p className="text-gray-600 mb-4">View all requests made by this user</p>
                </div>
                <button
                  onClick={() => navigate(`/user-requests/${user.id}?userType=${user.userType}`)}
                  className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg transition-colors shadow-md font-medium"
                >
                  <FaClipboardList className="text-xl" />
                  View User Requests
                </button>
              </div>
            </div>
          </div>

          {/* Balance and Transactions */}
          <div
            id="transactions-section"
            className="mt-10 bg-white p-6 rounded-xl shadow-lg border-2 border-gray-300 space-y-8 scroll-mt-24"
          >
            {/* User Balance */}
            <div className="bg-gradient-to-r from-green-400 to-blue-500 p-6 rounded-xl text-white shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-xl font-bold">User Wallet Balance</h4>
                  <p className="text-3xl mt-2 font-semibold">₨ {userBalance.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <button
                    onClick={handleRefreshTransactions}
                    className="flex items-center gap-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
                    disabled={transactionsLoading}
                  >
                    <FaSync className={`${transactionsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Transaction Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border-2 border-green-200 p-4 rounded-lg">
                <h5 className="text-green-800 font-semibold">Total Credits</h5>
                <p className="text-2xl font-bold text-green-600">
                  {transactions.filter(t => t.type === 'credit').length}
                </p>
                <p className="text-sm text-green-600">
                  ₨ {transactions.filter(t => t.type === 'credit').reduce((sum, t) => sum + (t.amount || 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-red-50 border-2 border-red-200 p-4 rounded-lg">
                <h5 className="text-red-800 font-semibold">Total Debits</h5>
                <p className="text-2xl font-bold text-red-600">
                  {transactions.filter(t => t.type === 'debit').length}
                </p>
                <p className="text-sm text-red-600">
                  ₨ {transactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + (t.amount || 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-lg">
                <h5 className="text-blue-800 font-semibold">Total Transactions</h5>
                <p className="text-2xl font-bold text-blue-600">{transactions.length}</p>
                <p className="text-sm text-blue-600">All time</p>
              </div>
            </div>

            {/* Transaction Table */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-2xl font-bold text-gray-800">Transaction History</h4>
                {transactionsLoading && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <FaSpinner className="animate-spin" />
                    <span>Loading...</span>
                  </div>
                )}
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto text-sm text-left text-gray-700 border-2 border-gray-300 rounded-xl">
                  <thead className="bg-gray-100 text-gray-800">
                    <tr>
                      <th className="px-4 py-3 border font-semibold">Date & Time</th>
                      <th className="px-4 py-3 border font-semibold">Transaction ID</th>
                      <th className="px-4 py-3 border font-semibold">Type</th>
                      <th className="px-4 py-3 border font-semibold">Amount (₨)</th>
                      <th className="px-4 py-3 border font-semibold">Balance Before</th>
                      <th className="px-4 py-3 border font-semibold">Balance After</th>
                      <th className="px-4 py-3 border font-semibold">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length > 0 ? (
                      transactions.map((txn, index) => (
                        <tr key={index} className="hover:bg-gray-50 border-b">
                          <td className="px-4 py-3 border">
                            {formatTransactionDate(txn.createdAt)}
                          </td>
                          <td className="px-4 py-3 border font-mono text-xs">
                            #{txn.id.slice(-8).toUpperCase()}
                          </td>
                          <td className="px-4 py-3 border">
                            <div className="flex items-center gap-2">
                              {getTransactionIcon(txn.type)}
                              <span className={`font-semibold capitalize ${getTransactionColor(txn.type)}`}>
                                {txn.type}
                              </span>
                            </div>
                          </td>
                          <td className={`px-4 py-3 border font-bold ${getTransactionColor(txn.type)}`}>
                            {txn.type === 'credit' ? '+' : '-'}₨ {(txn.amount || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 border">
                            ₨ {(txn.balanceBefore || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 border font-semibold">
                            ₨ {(txn.balanceAfter || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 border">
                            <div className="max-w-xs truncate" title={txn.description}>
                              {txn.description || 'No description'}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-8 text-gray-500">
                          {transactionsLoading ? (
                            <div className="flex items-center justify-center gap-2">
                              <FaSpinner className="animate-spin" />
                              Loading transactions...
                            </div>
                          ) : (
                            "No transactions found for this user."
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mt-8">
            {!user.isverified && (
              <button
                onClick={handleVerifyUser}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all shadow-md"
              >
                Verify User
              </button>
            )}
            <button
              onClick={handleDeleteUser}
              className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all shadow-md"
            >
              Delete User
            </button>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={handleCloseExpandedImage}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={handleCloseExpandedImage}
              className="absolute top-4 right-4 bg-white rounded-full p-2 text-gray-800 hover:bg-gray-200 transition-colors"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={expandedImage || "/placeholder.svg"}
              alt="Expanded view"
              className="max-h-[85vh] max-w-full mx-auto object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default UserDetailsPage